// @ts-nocheck
'use client';

import React, { useEffect, useState } from 'react';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { beneficiarySchema, fallbackBeneficiarySchema } from '@/lib/schema';
import { useWillStore } from '@/lib/store';
import { Input } from '@/app/components/ui/Input';
import { Label } from '@/app/components/ui/Label';
import { Button } from '@/app/components/ui/Button';
import { Checkbox } from '@/app/components/ui/Checkbox';
import { Modal } from '@/app/components/ui/Modal';
import { FormNavigation } from '../FormNavigation';
import { AddressSection } from '../AddressSection';
import * as yup from 'yup';
import { StepWrapper } from '../StepWrapper';
import { CharityAutocomplete } from '../CharityAutocomplete';
import { AddressAutocomplete } from '../AddressAutocomplete';
import { Pencil, Users, User, ChevronDown, UserMinus, ShieldCheck, Check } from 'lucide-react';
import { ActionCard } from '@/app/components/ui/ActionCard';
import { RELATIONSHIP_OPTIONS } from '@/lib/constants';
import { useSavedPeople, SAVED_PEOPLE_PRESETS } from '@/lib/hooks/useSavedPeople';
import { AgeSelection } from '../shared/AgeSelection';

// Optional address schema for beneficiaries - no required fields
const optionalAddressSchema = yup.object({
    name: yup.string().optional(),
    line1: yup.string().optional(),
    line2: yup.string().optional(),
    city: yup.string().optional(),
    postcode: yup.string().optional(),
}).optional().nullable();

// Reuse schemas but wrapped - override required fields for step-by-step flow
const localBeneficiarySchema = beneficiarySchema.shape({
    fullName: yup.string().optional(), // Validation handled in modal
    relationship: yup.string().optional(), // Not required - auto-set or optional
    percentage: yup.number().min(0).max(100).optional().transform((value, originalValue) => {
        // Handle empty string or NaN - always return number, not null
        if (originalValue === '' || originalValue === null || originalValue === undefined) return 0;
        return isNaN(value) ? 0 : value;
    }),
    predeceasePolicy: yup.string().optional().transform((value) => {
        // Allow empty/undefined values - this gets set on step 2
        if (!value || value === '') return undefined;
        return value;
    }),
    isOver18: yup.boolean().optional().nullable().transform((value, originalValue) => {
        if (originalValue === '') return null;
        return value;
    }), // Not required during initial add
    substitutes: yup.array().of(fallbackBeneficiarySchema).optional().default([]),
    // Override address to be fully optional for charities
    address: optionalAddressSchema
});

const beneficiariesStepSchema = yup.object({
    formStep: yup.number().default(1),
    beneficiaries: yup.array().of(localBeneficiarySchema).min(1, 'At least one beneficiary is required').required()
        .when('formStep', {
            is: (step: number) => step >= 2,
            then: (schema) => schema.test('sum-100', 'Total percentage must equal 100%', (value) => {
                if (!value) return true;
                const total = value.reduce((sum, b) => sum + (Number(b.percentage) || 0), 0);
                return Math.abs(total - 100) < 0.1;
            }),
            otherwise: (schema) => schema
        })
});

type BeneficiariesData = yup.InferType<typeof beneficiariesStepSchema>;

type DistributionMode = 'select' | 'single' | 'charity' | 'split_equal' | 'split_custom';

// Internal component for adding a new beneficiary with isolated form state
const AddBeneficiaryForm = ({
    beneficiary,
    onSave,
    showPercentage,
    maxPercentage
}: {
    beneficiary: any,
    onSave: (data: any) => void,
    showPercentage?: boolean,
    maxPercentage?: number
}) => {
    const isFullName = (name: string | undefined): boolean => {
        if (!name || name.trim() === '') return false;
        return name.trim().indexOf(' ') !== -1;
    };

    const { register, control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
        mode: 'onChange',
        defaultValues: {
            fullName: beneficiary.fullName || '',
            relationship: beneficiary.relationship || 'Beneficiary',
            address: beneficiary.address || { line1: '', line2: '', city: '', postcode: '' },
            isOver18: beneficiary.isOver18,
            percentage: beneficiary.percentage || ''
        }
    });

    const fullName = watch('fullName');
    const isOver18 = watch('isOver18');

    const isValid = fullName &&
        isFullName(fullName) &&
        isOver18 !== undefined &&
        // Address validation
        !!(watch('address.line1') && watch('address.city') && watch('address.postcode'));

    return (
        <div className="space-y-6">
            <div className="space-y-3">
                <Label className="text-base font-medium text-gray-700">Full Name</Label>
                <Input
                    {...register('fullName')}
                    placeholder=""
                />
                {fullName && !isFullName(fullName) && (
                    <p className="text-destructive text-sm">Please enter at least a first and last name (e.g. &quot;John Smith&quot;)</p>
                )}
            </div>

            <div className="space-y-2">
                <AddressSection
                    prefix="address"
                    label="Address"
                    labelClassName="text-base font-medium text-gray-700"
                    register={register}
                    setValue={setValue}
                    errors={errors}
                    control={control}
                />
            </div>

            <AgeSelection
                value={isOver18}
                onChange={(val) => setValue('isOver18', val, { shouldValidate: true })}
            />

            {showPercentage && (
                <div className="space-y-2">
                    <Label className="text-base font-medium text-gray-700">Percentage of your estate (%)</Label>
                    <Input
                        type="number"
                        {...register('percentage')}
                        placeholder={`e.g. ${Math.min(50, maxPercentage || 100)}`}
                        min="1"
                        max={maxPercentage || 100}
                        step="0.01"
                    />
                    {maxPercentage !== undefined && (
                        <p className="text-sm text-muted-foreground">
                            {maxPercentage}% remaining to allocate
                        </p>
                    )}
                </div>
            )}

            <div className="pt-4">
                <Button
                    type="button"
                    onClick={handleSubmit(onSave)}
                    disabled={!isValid}
                    variant="primary"
                    className="w-full"
                >
                    Save
                </Button>
            </div>
        </div>
    );
};

// Internal component for adding a fallback beneficiary with isolated form state
const AddFallbackBeneficiaryForm = ({
    fallback,
    isCharity,
    maxPercentage,
    onSave
}: {
    fallback: any,
    isCharity: boolean,
    maxPercentage: number,
    onSave: (data: any) => void
}) => {
    const isFullName = (name: string | undefined): boolean => {
        if (!name || name.trim() === '') return false;
        return name.trim().indexOf(' ') !== -1;
    };

    const { register, control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
        mode: 'onChange',
        defaultValues: {
            fullName: fallback.fullName || '',
            charityNumber: fallback.charityNumber || '',
            address: fallback.address || { line1: '', line2: '', city: '', postcode: '' },
            isOver18: fallback.isOver18,
            percentage: fallback.percentage || ''
        }
    });

    const fullName = watch('fullName');
    const isOver18 = watch('isOver18');
    const percentage = watch('percentage');

    const currentVal = parseFloat(percentage?.toString() || '0') || 0;
    const isOverLimit = currentVal > maxPercentage + 0.01;

    const isValid = isCharity
        ? (!!fullName && fullName.trim() && !!percentage)
        : (!!fullName && isFullName(fullName) && isOver18 !== undefined && !!percentage &&
            // Address validation for non-charity
            !!(watch('address.line1') && watch('address.city') && watch('address.postcode')));

    return (
        <div className="space-y-6">
            {isCharity ? (
                <>
                    <div className="space-y-2">
                        <Label className="text-base font-medium text-gray-700">Charity Name</Label>
                        <CharityAutocomplete
                            onCharitySelect={(charity) => {
                                setValue('fullName', charity.name);
                                setValue('charityNumber', charity.number);
                            }}
                            defaultValue={fullName || ''}
                            value={fullName || ''}
                            onChange={(e) => setValue('fullName', e.target.value)}
                            name="fullName"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-base font-medium text-gray-700">Charity Number (Optional)</Label>
                        <Input
                            {...register('charityNumber')}
                            placeholder="e.g. 12345678"
                        />
                    </div>
                </>
            ) : (
                <>
                    <div className="space-y-2">
                        <Label className="text-base font-medium text-gray-700">Full Name</Label>
                        <Input
                            {...register('fullName')}
                            placeholder=""
                        />
                        {fullName && !isFullName(fullName) && (
                            <p className="text-destructive text-sm">Please enter at least a first and last name (e.g. &quot;John Smith&quot;)</p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <AddressSection
                            prefix="address"
                            label="Address"
                            labelClassName="text-base font-medium text-gray-700"
                            register={register}
                            setValue={setValue}
                            errors={errors}
                            control={control}
                        />
                    </div>

                    <AgeSelection
                        value={isOver18}
                        onChange={(val) => setValue('isOver18', val, { shouldValidate: true })}
                        name="fallback-isOver18"
                    />
                </>
            )}

            <div className="space-y-2">
                <div className="flex justify-between items-center">
                    <Label className="text-base font-medium text-gray-700">Share (%)</Label>
                    <span className={`text-sm ${isOverLimit ? 'text-destructive font-bold' : 'text-gray-500'}`}>
                        Max allowed: {maxPercentage}%
                    </span>
                </div>
                <Input
                    type="number"
                    {...register('percentage')}
                    placeholder="%"
                    min="1"
                    max={maxPercentage}
                    step="0.01"
                    className={isOverLimit ? "border-destructive focus-visible:ring-destructive" : ""}
                />
                {isOverLimit && (
                    <p className="text-sm text-destructive font-medium">
                        Total allocation cannot exceed 100%
                    </p>
                )}
            </div>

            <div className="pt-4">
                <Button
                    type="button"
                    onClick={handleSubmit(onSave)}
                    disabled={!isValid}
                    variant="primary"
                    className="w-full"
                >
                    Save
                </Button>
            </div>
        </div>
    );
};

import { PersonForm } from '../shared/PersonForm';
import { QuickAddPeople } from '../shared/QuickAddPeople';

export const Beneficiaries = () => {
    const { data, setData, setStep, setStepBack, isEditingFromReview, setIsEditingFromReview, navigatingBack, clearNavigatingBack, addManuallyAddedPerson, removeManuallyAddedPerson, setEstateCompleted, updatePerson } = useWillStore();

    const sanitizeBeneficiaries = (list: any[]) => {
        if (!list) return [];
        return list.map(b => ({
            ...b,
            address: b.address === null ? undefined : b.address,
            isOver18: b.isOver18 === null ? undefined : b.isOver18,
            predeceasePolicy: b.predeceasePolicy === null ? undefined : b.predeceasePolicy,
            substitutes: b.substitutes?.map((s: any) => ({
                ...s,
                address: s.address === null ? undefined : s.address
            }))
        }));
    };

    // Use shared hook to get saved people for quick add
    const savedPeople = useSavedPeople(SAVED_PEOPLE_PRESETS.beneficiaries);

    // Track manually added people so they persist even when unticked - NOW USING STORE
    const manuallyAddedPeople = data.manuallyAddedPeople || [];

    // Get all available people for beneficiary selection (saved people + manually added people)
    const getAllBeneficiaryPeople = () => {
        // Start with saved people but merge any updated fields from manuallyAddedPeople
        const allPeople = savedPeople.map((person: any) => {
            const manuallyAdded = manuallyAddedPeople.find((p: any) => p.fullName === person.fullName);
            if (manuallyAdded) {
                // Merge: prefer manually updated values over saved values
                return {
                    ...person,
                    address: manuallyAdded.address || person.address,
                    isOver18: manuallyAdded.isOver18 ?? person.isOver18
                };
            }
            return person;
        });

        // Add any manually-added people that aren't in the saved people list
        manuallyAddedPeople.forEach((person: any) => {
            if (person?.fullName && !allPeople.some(p => p.fullName === person.fullName)) {
                allPeople.push({
                    fullName: person.fullName,
                    relationship: person.relationship || 'Beneficiary',
                    address: person.address,
                    isOver18: person.isOver18
                });
            }
        });

        // Filter out the main person (About You)
        return allPeople.filter(person => person.fullName !== data.personalDetails?.fullName);
    };

    // Check if a person is already a beneficiary
    const isBeneficiarySelected = (personName: string) => {
        return beneficiaries?.some(b => b.fullName === personName) || false;
    };

    // Add person to manually added list (for persistence)
    const trackManuallyAddedPerson = (person: { fullName: string; relationship: string; address?: any; isOver18?: boolean }) => {
        if (!savedPeople.some((p: any) => p.fullName === person.fullName) &&
            !manuallyAddedPeople.some((p: any) => p.fullName === person.fullName)) {
            addManuallyAddedPerson(person);
        }
    };

    // Toggle beneficiary selection
    const toggleBeneficiary = (person: { fullName: string; relationship: string; address?: any; isOver18?: boolean }) => {
        const existingIndex = beneficiaries?.findIndex(b => b.fullName === person.fullName);
        if (existingIndex !== undefined && existingIndex >= 0) {
            remove(existingIndex);
        } else {
            // Calculate share - if first person 100%, else 0% (to be edited)
            const share = fields.length === 0 ? 100 : 0;
            const isCharity = person.relationship === 'Charity';
            append({
                fullName: person.fullName,
                relationship: person.relationship,
                percentage: share,
                isOver18: isCharity ? undefined : (person.isOver18 ?? true),
                // Charities don't need an address
                address: isCharity ? undefined : {
                    line1: person.address?.line1 || '',
                    line2: person.address?.line2 || '',
                    city: person.address?.city || '',
                    postcode: person.address?.postcode || ''
                },
                predeceasePolicy: undefined,
                substitutes: []
            });
        }
    };

    const {
        register,
        control,
        handleSubmit,
        setValue,
        watch,
        trigger,
        reset,
        getValues,
        formState: { errors },
    } = useForm<BeneficiariesData>({
        resolver: yupResolver(beneficiariesStepSchema) as any,
        mode: 'onBlur',
        shouldUnregister: false,
        defaultValues: {
            formStep: 1,
            beneficiaries: data.beneficiaries?.length ? data.beneficiaries.map((b: any) => ({
                ...b,
                predeceasePolicy: (b.predeceasePolicy as "redistribute" | "similar_goals" | "substitute" | "children") || (b.relationship === 'Charity' ? 'similar_goals' : 'children'),
                substitutes: (b as any).substitutes || []
            })) : []
        },
    });

    // Sync form with store data when it changes (e.g. after login/rehydration)
    useEffect(() => {
        // Only reset if we have data to show
        if (data.beneficiaries) {
            const currentStep = getValues('formStep');
            reset({
                formStep: currentStep,
                beneficiaries: data.beneficiaries?.length ? data.beneficiaries.map((b: any) => ({
                    ...b,
                    predeceasePolicy: (b.predeceasePolicy as "redistribute" | "similar_goals" | "substitute" | "children") || (b.relationship === 'Charity' ? 'similar_goals' : 'children'),
                    substitutes: (b as any).substitutes || []
                })) : []
            });
        }
    }, [data.beneficiaries, reset, getValues]);

    const { fields, append, remove, replace, update } = useFieldArray({
        control,
        name: 'beneficiaries',
    });

    const beneficiaries = useWatch({ control, name: 'beneficiaries' });
    const formStep = useWatch({ control, name: 'formStep' });

    // Handle navigating back from next section - go to last sub-step
    useEffect(() => {
        if (navigatingBack) {
            // Go to the last sub-step (3 = fallback scenarios)
            setValue('formStep', 3);
            clearNavigatingBack();
        }
    }, [navigatingBack, clearNavigatingBack, setValue]);

    const personalDetails = data.personalDetails;
    const isMarriedOrCivil = personalDetails?.maritalStatus === 'married' || personalDetails?.maritalStatus === 'civil_partnership';
    const spouseFullName = (personalDetails as any)?.spouseFullName;

    // State for editing quick add people
    const [editingQuickAddPerson, setEditingQuickAddPerson] = useState<any>(null);
    const [tempQuickAddPerson, setTempQuickAddPerson] = useState<any>(null);
    const [manualQuickAddAddressMode, setManualQuickAddAddressMode] = useState(false);

    // Custom mode state map
    const [customModeMap, setCustomModeMap] = useState<Record<string, boolean>>({});

    const [interventionStep, setInterventionStep] = useState<'none' | 'spouse_children'>('none');
    const [interventionChoice, setInterventionChoice] = useState<'spouse_first' | 'spouse_and_children' | null>(null);

    // Determine initial mode based on existing data
    const [mode, setMode] = useState<DistributionMode>(() => {
        // If no beneficiaries, default to split_custom (builder mode)
        if (!data.beneficiaries || data.beneficiaries.length === 0) return 'split_custom';

        // If 1 beneficiary (100%)
        if (data.beneficiaries.length === 1 && data.beneficiaries[0].percentage === 100) {
            // Check if charity
            if (data.beneficiaries[0].relationship === 'Charity') return 'charity';
            return 'single';
        }

        // If > 1
        if (data.beneficiaries.length > 1) {
            // Check if equal
            const firstPct = data.beneficiaries[0].percentage;
            const isEqual = data.beneficiaries.every(b => Math.abs((b.percentage || 0) - (firstPct || 0)) < 0.1);
            // Default to custom to allow flexibility
            return 'split_custom';
        }

        return 'split_custom';
    });


    // Check if primary beneficiary is the spouse (for fallback wording)
    const isPrimaryBeneficiarySpouse = mode === 'single' && beneficiaries?.[0] && (
        beneficiaries[0].fullName === spouseFullName ||
        beneficiaries[0].relationship === 'Spouse' ||
        beneficiaries[0].relationship === 'Partner'
    );

    // Calculate percentages for equal split whenever list changes
    useEffect(() => {
        if (mode === 'split_equal' && beneficiaries && beneficiaries.length > 0) {
            const count = beneficiaries.length;
            // Calculate base share rounded down to 2 decimal places
            const baseShare = Math.floor((100 / count) * 100) / 100;

            let currentSum = 0;
            beneficiaries.forEach((_, index) => {
                let val = baseShare;

                // For the last item, give the remainder to ensure exactly 100%
                if (index === count - 1) {
                    val = 100 - currentSum;
                    // Fix floating point precision
                    val = parseFloat(val.toFixed(2));
                }

                if (beneficiaries[index].percentage !== val) {
                    setValue(`beneficiaries.${index}.percentage`, val);
                }
                // Use a fixed precision addition to avoid tiny floating point errors accumulating
                currentSum = parseFloat((currentSum + val).toFixed(2));
            });
        } else if (mode === 'single' && beneficiaries && beneficiaries.length === 1) {
            if (beneficiaries[0].percentage !== 100) {
                setValue(`beneficiaries.0.percentage`, 100);
            }
        }
    }, [beneficiaries?.length, mode, setValue]);

    // Handle Mode Selection
    const handleModeSelect = (newMode: DistributionMode) => {
        setMode(newMode);

        // Reset beneficiaries based on mode
        if (newMode === 'single') {
            if (fields.length === 0) {
                append({ fullName: '', relationship: 'Beneficiary', percentage: 100, isOver18: true, address: { line1: '', line2: '', city: '', postcode: '' }, predeceasePolicy: 'children', substitutes: [] });
            } else {
                const first = beneficiaries[0];
                replace([{ ...first, percentage: 100, substitutes: (first as any).substitutes || [] }]);
            }
        } else if (newMode === 'charity') {
            if (fields.length === 0) {
                append({ fullName: '', relationship: 'Charity', percentage: 100, isOver18: true, address: { line1: '', line2: '', city: '', postcode: '' }, predeceasePolicy: 'similar_goals', substitutes: [] });
            } else {
                replace([{ fullName: '', relationship: 'Charity', percentage: 100, isOver18: true, address: { line1: '', line2: '', city: '', postcode: '' }, predeceasePolicy: 'similar_goals', substitutes: [] }]);
            }
        } else if (newMode === 'split_equal' || newMode === 'split_custom') {
            if (fields.length === 0) {
                append({ fullName: '', relationship: 'Beneficiary', percentage: 0, isOver18: true, address: { line1: '', line2: '', city: '', postcode: '' }, predeceasePolicy: 'children', substitutes: [] });
                append({ fullName: '', relationship: 'Beneficiary', percentage: 0, isOver18: true, address: { line1: '', line2: '', city: '', postcode: '' }, predeceasePolicy: 'children', substitutes: [] });
            }
        }
    };

    const handleRelationshipSelect = (index: number, value: string) => {
        const fieldId = fields[index].id;
        const pathPrefix = `beneficiaries.${index}`;

        if (value === 'Other') {
            setCustomModeMap(prev => ({ ...prev, [fieldId]: true }));
            // @ts-ignore
            setValue(`${pathPrefix}.relationship`, '');
        } else {
            setCustomModeMap(prev => ({ ...prev, [fieldId]: false }));
            // @ts-ignore
            setValue(`${pathPrefix}.relationship`, value);
        }
    };

    const clearCustomMode = (index: number) => {
        const fieldId = fields[index].id;
        const pathPrefix = `beneficiaries.${index}`;
        setCustomModeMap(prev => ({ ...prev, [fieldId]: false }));
        // @ts-ignore
        setValue(`${pathPrefix}.relationship`, '');
    };

    const addSpouse = (index: number) => {
        if (!spouseFullName) return;
        setValue(`beneficiaries.${index}.fullName`, spouseFullName);
        const status = personalDetails?.maritalStatus;
        const rel = status === 'civil_partnership' ? 'Partner' : 'Spouse';
        setValue(`beneficiaries.${index}.relationship`, rel);
        // Address logic...
        // @ts-ignore
        const address = personalDetails?.spouseLivesWithYou === false ? personalDetails?.spouseAddress : personalDetails?.address;
        if (address) {
            setValue(`beneficiaries.${index}.address.line1` as any, address.line1 || '');
            setValue(`beneficiaries.${index}.address.line2` as any, address.line2 || '');
            setValue(`beneficiaries.${index}.address.city` as any, address.city || '');
            setValue(`beneficiaries.${index}.address.postcode` as any, address.postcode || '');
        }
    };

    const openEditQuickAddModal = (person: any, e: React.MouseEvent) => {
        e.stopPropagation(); // Prevent toggling selection
        setEditingQuickAddPerson(person);
        setTempQuickAddPerson({
            fullName: person.fullName,
            relationship: person.relationship,
            address: person.address || { line1: '', line2: '', city: '', postcode: '' },
            isOver18: person.isOver18 ?? true // Preserve actual value, default to true if not set
        });
        setManualQuickAddAddressMode(false);
    };

    const handleCloseQuickAddModal = () => {
        setEditingQuickAddPerson(null);
        setTempQuickAddPerson(null);
    };

    const handleSaveQuickAddPerson = () => {
        if (editingQuickAddPerson && tempQuickAddPerson) {
            updatePerson(editingQuickAddPerson.fullName, {
                fullName: tempQuickAddPerson.fullName,
                relationship: tempQuickAddPerson.relationship,
                address: tempQuickAddPerson.address
            });
            handleCloseQuickAddModal();
        }
    };

    // Submission
    const [fallbackSubmitAttempted, setFallbackSubmitAttempted] = React.useState(false);
    const [step1SubmitAttempted, setStep1SubmitAttempted] = React.useState(false);

    const onInvalid = (errors: any) => {
        if (formStep === 2) setFallbackSubmitAttempted(true);
        if (formStep === 1) setStep1SubmitAttempted(true);
        console.log('Form validation errors:', errors);
    };

    const onSubmit = (formData: BeneficiariesData) => {
        if (formStep === 1) {
            if (interventionStep === 'none') {
                const hasSpouse = formData.beneficiaries?.some(b =>
                    b.relationship === 'Spouse' || b.relationship === 'Partner' || b.relationship === 'Spouse/Partner'
                );
                const hasChildren = formData.beneficiaries?.some(b => b.relationship === 'Child');

                // Check if there are ONLY spouse and children (no charities or other individuals)
                const hasOtherBeneficiaries = formData.beneficiaries?.some(b => {
                    const rel = b.relationship;
                    // Not spouse/partner and not child = other beneficiary
                    return rel !== 'Spouse' &&
                        rel !== 'Partner' &&
                        rel !== 'Spouse/Partner' &&
                        rel !== 'Child';
                });

                // Only show intervention if it's EXCLUSIVELY spouse + children
                if (hasSpouse && hasChildren && !hasOtherBeneficiaries) {
                    setInterventionStep('spouse_children');
                    window.scrollTo(0, 0);
                    return;
                }
            }

            // Calculate equal shares for all beneficiaries when moving to allocation step
            const currentBeneficiaries = formData.beneficiaries;
            let updatedBeneficiaries = [...currentBeneficiaries];

            if (updatedBeneficiaries.length > 0) {
                const count = updatedBeneficiaries.length;
                // Calculate base share rounded down to 2 decimal places
                const baseShare = Math.floor((100 / count) * 100) / 100;
                let currentSum = 0;

                updatedBeneficiaries = updatedBeneficiaries.map((b, index) => {
                    let val = baseShare;
                    if (index === count - 1) {
                        val = 100 - currentSum;
                        // float precision fix
                        val = parseFloat(val.toFixed(2));
                    }
                    // Use a fixed precision addition to avoid tiny floating point errors accumulating
                    currentSum = parseFloat((currentSum + val).toFixed(2));
                    return { ...b, percentage: val };
                });

                setValue('beneficiaries', updatedBeneficiaries);
            }

            // If only one beneficiary, skip Step 2 (Allocation) and go directly to Step 3 (Fallback)
            if (updatedBeneficiaries.length === 1) {
                setValue('formStep', 3);
                window.scrollTo(0, 0);
                // Persist data
                setData({
                    beneficiaries: sanitizeBeneficiaries(updatedBeneficiaries),
                });
                return;
            }

            // Step 1 -> Step 2 (Allocation)

            setValue('formStep', 2);
            window.scrollTo(0, 0);
            // Persist data
            setData({
                beneficiaries: sanitizeBeneficiaries(updatedBeneficiaries),
            });
            return;
        }

        if (formStep === 2) {
            // Step 2 (Allocation only) -> Step 3 (Fallback questions)

            // Save current allocation
            const currentBeneficiaries = watch('beneficiaries');
            setData({ beneficiaries: sanitizeBeneficiaries(currentBeneficiaries) });

            setValue('formStep', 3); // Go to Fallback questions
            window.scrollTo(0, 0);
            return;
        }

        if (formStep === 3) {
            // Step 3 (Fallback questions) -> Finish estate section

            // Apply defaults for survivorship policy if not already set
            const currentBeneficiaries = watch('beneficiaries');
            const updatedBeneficiaries = currentBeneficiaries.map(b => ({
                ...b,
                predeceasePolicy: b.predeceasePolicy || (b.relationship === 'Charity' ? 'similar_goals' : 'children')
            }));

            setValue('beneficiaries', updatedBeneficiaries);
            setData({ beneficiaries: sanitizeBeneficiaries(updatedBeneficiaries) });
            window.scrollTo(0, 0);

            if (isEditingFromReview) {
                setIsEditingFromReview(false);
                setStep(5);
            } else {
                // Check if all sections are now complete - if so, go to Review
                // Use setTimeout to ensure state has updated before checking
                setTimeout(() => {
                    // Import is at top of file
                    const { isAllSectionsComplete } = require('@/lib/store');
                    if (isAllSectionsComplete()) {
                        setStep(5); // Go to Review
                    } else {
                        setStep(3); // Executors
                    }
                }, 0);
            }
            return;
        }
    };

    const handleBack = () => {
        if (interventionStep !== 'none') {
            setInterventionStep('none');
            return;
        }
        if (formStep === 3) {
            // If only one beneficiary, skip back to step 1 instead of step 2
            const currentBeneficiaries = watch('beneficiaries');
            if (currentBeneficiaries && currentBeneficiaries.length === 1) {
                setValue('formStep', 1);
            } else {
                setValue('formStep', 2);
            }
            window.scrollTo(0, 0);
        } else if (formStep === 2) {
            setValue('formStep', 1);
            window.scrollTo(0, 0);
        } else {
            setStepBack(1); // Go back to last page of Children/Guardians
        }
    };

    const customTotalPercentage = beneficiaries?.reduce((sum, b) => sum + (Number(b.percentage) || 0), 0) || 0;

    const [tempBeneficiary, setTempBeneficiary] = useState<any>(null);

    const handleSaveCharity = () => {
        if (editingCharityIndex !== null && tempBeneficiary) {
            // Ensure relationship is set to 'Charity' and predeceasePolicy to 'similar_goals'
            const charityData = {
                ...tempBeneficiary,
                relationship: 'Charity',
                predeceasePolicy: tempBeneficiary.predeceasePolicy || 'similar_goals',
                isOver18: undefined, // Charities don't have age
                address: undefined, // Charities don't need a personal address
            };
            update(editingCharityIndex, charityData);
            // Track charity so it persists in quick add even if unticked
            if (tempBeneficiary.fullName) {
                trackManuallyAddedPerson({
                    fullName: tempBeneficiary.fullName,
                    relationship: 'Charity',
                    address: undefined
                });
            }
        }
        setEditingCharityIndex(null);
        setTempBeneficiary(null);
    };
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editingCharityIndex, setEditingCharityIndex] = useState<number | null>(null);
    const [editingFallbackIndex, setEditingFallbackIndex] = useState<number | null>(null);
    const [targetBeneficiaryIndex, setTargetBeneficiaryIndex] = useState<number | null>(null);
    const [fallbackModalType, setFallbackModalType] = useState<'individual' | 'charity' | null>(null);
    const [isEditingFallback, setIsEditingFallback] = useState(false);
    const [intestacyConfirmed, setIntestacyConfirmed] = useState(false);

    // New state for adding mode (without auto-selecting)
    const [addingNewPersonMode, setAddingNewPersonMode] = useState(false);
    const [addingNewCharityMode, setAddingNewCharityMode] = useState(false);

    // Render Logic
    const renderInputForm = (index: number) => {
        const currentVal = beneficiaries?.[index]?.relationship || '';
        const fieldId = fields[index].id;
        const isCustom = customModeMap[fieldId] || (currentVal && !RELATIONSHIP_OPTIONS.includes(currentVal));
        const selectValue = isCustom ? 'Other' : currentVal;

        const isCharityMode = mode === 'charity';
        const isSpouseRow = isMarriedOrCivil && (currentVal === 'Spouse' || currentVal === 'Partner' || currentVal === 'Spouse/Partner');
        const hasData = !!beneficiaries?.[index]?.fullName;

        if (isCharityMode) {
            const charityHasData = !!beneficiaries?.[index]?.fullName;
            return (
                <div key={fields[index].id} className="p-4 border border-gray-200 rounded-md bg-white relative mb-4 shadow-sm">

                    {/* Hidden inputs to persist data */}
                    <div className="hidden">
                        <Input {...register(`beneficiaries.${index}.fullName`)} />
                        <Input {...register(`beneficiaries.${index}.charityNumber`)} />
                        <input type="hidden" {...register(`beneficiaries.${index}.relationship`)} value="Charity" />
                        <input type="hidden" {...register(`beneficiaries.${index}.isOver18`)} value="true" />
                    </div>

                    {!charityHasData ? (
                        <ActionCard showPlusIcon halfWidth onClick={() => setEditingCharityIndex(index)}>Add Charity</ActionCard>
                    ) : (
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
                            <div>
                                <p className="font-medium text-xl text-gray-900">{beneficiaries[index]?.fullName}</p>
                                <p className="text-sm text-gray-500">
                                    Charity {beneficiaries[index]?.charityNumber ? `• #${beneficiaries[index]?.charityNumber}` : ''}
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setEditingCharityIndex(index)}
                                    className="text-gray-500 hover:text-primary h-8 px-2 text-xs"
                                >
                                    <Pencil className="h-3.5 w-3.5 mr-1" />
                                    Edit
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        remove(index);
                                    }}
                                    className="text-gray-500 hover:text-destructive h-8 px-2 text-xs"
                                >
                                    Remove
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            );
        }

        // Individual Mode (Single or Split)
        return (
            <div key={fields[index].id} className="p-3 border border-gray-200 rounded-md bg-white relative mb-3 shadow-sm">
                {!hasData ? null : (
                    // SUMMARY STATE
                    <div className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg border border-gray-100">
                        <div>
                            <p className="font-medium text-base text-gray-900">{beneficiaries[index].fullName}</p>
                            {beneficiaries[index].relationship && beneficiaries[index].relationship !== 'Beneficiary' && (
                                <p className="text-xs text-gray-500">{beneficiaries[index].relationship}</p>
                            )}
                            {/* Summary for Step 1 - No percentage */}
                            {mode === 'split_custom' && formStep >= 2 && (
                                <p className="text-xs font-medium text-primary mt-0.5">{beneficiaries[index].percentage}% Share</p>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    if (beneficiaries?.[index]?.relationship === 'Charity') {
                                        setEditingCharityIndex(index);
                                    } else {
                                        setEditingIndex(index);
                                    }
                                }}
                                className="text-gray-500 hover:text-primary h-8 px-2 text-xs"
                            >
                                <Pencil className="h-3.5 w-3.5 mr-1" />
                                Edit
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    remove(index);
                                }}
                                className="text-gray-500 hover:text-destructive h-8 px-2 text-xs"
                            >
                                Remove
                            </Button>
                        </div>
                    </div>
                )}

                {/* Hidden Inputs to ensure data persists and is submitted */}
                <div className="hidden">
                    <Input {...register(`beneficiaries.${index}.fullName`)} />
                    <Input {...register(`beneficiaries.${index}.relationship`)} />
                    <Input {...register(`beneficiaries.${index}.percentage`)} />
                    <input type="hidden" {...register(`beneficiaries.${index}.isOver18`)} value={beneficiaries?.[index]?.relationship === 'Charity' ? 'true' : (watch(`beneficiaries.${index}.isOver18`)?.toString() || '')} />
                    <Input {...register(`beneficiaries.${index}.predeceasePolicy`)} />
                    <AddressSection
                        prefix={`beneficiaries.${index}.address`}
                        label="Address"
                        labelClassName="text-base font-medium text-gray-700"
                        register={register}
                        setValue={setValue}
                        errors={errors}
                        control={control}
                    />
                </div>
            </div>
        );
    };

    return (
        <StepWrapper
            title={interventionStep === 'spouse_children'
                ? "How would you like to share out your Estate?"
                : formStep === 1
                    ? "Who shall inherit your estate?"
                    : formStep === 2
                        ? "How would you like to share out your Estate?"
                        : "What happens if a beneficiary dies before you?"
            }
            description={interventionStep === 'spouse_children'
                ? undefined
                : formStep === 1
                    ? "You can still make specific gifts, like, sentimental items or one-off cash amounts, to people or charities later."
                    : formStep === 2
                        ? "Allocate a percentage of your estate to each beneficiary."
                        : "Choose what happens to each beneficiary's share if they pass away before you."
            }
        >
            <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
                <input type="hidden" {...register('formStep')} />

                {formStep === 1 && interventionStep === 'spouse_children' && (
                    <div className="space-y-10 animate-fade-in">
                        <div className="space-y-4">
                            <h3 className="text-xl font-bold text-primary">Dividing your estate between your partner and children</h3>
                            <div className="space-y-4 text-gray-600">
                                <p>
                                    While you are free to distribute your estate however you wish, it&apos;s worth considering the practical impact on your loved ones.
                                </p>
                                <p>
                                    If your estate includes a shared home or other joint assets, splitting the ownership immediately can sometimes force a sale, which might affect your partner&apos;s living situation.
                                </p>

                                <div className="space-y-2">
                                    <h4 className="font-bold text-gray-900">Prioritising your partner first</h4>
                                    <p>
                                        It is very common for couples to leave everything to each other first, with children named as fallbacks. This provides your partner with total financial security and control over shared assets.
                                    </p>
                                    {(() => {
                                        const spouse = beneficiaries?.find(b => b.relationship === 'Spouse' || b.relationship === 'Partner' || b.relationship === 'Spouse/Partner');
                                        const children = beneficiaries?.filter(b => b.relationship === 'Child').map(b => b.fullName);
                                        if (!spouse) return null;
                                        return (
                                            <p>
                                                If you name {spouse.fullName} as the primary beneficiary, they will inherit your estate in full. They would then be responsible for providing for {children && children.length > 0 ? children.join(' and ') : 'your children'} in their own Will.
                                            </p>
                                        );
                                    })()}
                                </div>

                                <div className="space-y-2">
                                    <h4 className="font-bold text-gray-900">Naming your partner and children together</h4>
                                    <p>
                                        This is a popular choice for blended families, as it provides absolute certainty about what both your partner and children will receive.
                                    </p>
                                    <p>
                                        It is also a straightforward option if you don&apos;t have large, single assets (like a house) that are difficult to divide.
                                    </p>
                                    <p>
                                        If you select this, you will be able to set the exact percentage split on the next page.
                                    </p>
                                </div>
                            </div>

                            <div className="pt-6 space-y-4 w-full md:w-1/2">
                                <Label className="text-base font-medium text-gray-700">What would you like to do?</Label>
                                <div className="space-y-3">
                                    {(() => {
                                        const spouse = beneficiaries?.find(b => b.relationship === 'Spouse' || b.relationship === 'Partner' || b.relationship === 'Spouse/Partner');
                                        const children = beneficiaries?.filter(b => b.relationship === 'Child');
                                        const childNames = children?.map(c => c.fullName).join(', ');

                                        return (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => setInterventionChoice('spouse_first')}
                                                    className={`w-full text-left p-4 border-2 rounded-xl transition-all duration-300 ${interventionChoice === 'spouse_first' ? 'border-primary bg-primary/5 scale-[1.01]' : 'border-gray-200 bg-white hover:border-primary hover:bg-primary/5 hover:scale-[1.01]'}`}
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <div className={`h-6 w-6 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${interventionChoice === 'spouse_first' ? 'border-primary bg-primary text-white' : 'border-gray-300'}`}>
                                                            {interventionChoice === 'spouse_first' && <Check className="h-4 w-4" />}
                                                        </div>
                                                        <div className="flex flex-col min-w-0">
                                                            <span className="text-base font-semibold text-gray-900">Leave my estate to {spouse?.fullName || 'partner'} first</span>
                                                            <span className="text-sm text-muted-foreground">
                                                                If {spouse?.fullName || 'partner'} dies before me, leave my estate to {childNames || 'my children'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </button>

                                                <button
                                                    type="button"
                                                    onClick={() => setInterventionChoice('spouse_and_children')}
                                                    className={`w-full text-left p-4 border-2 rounded-xl transition-all duration-300 ${interventionChoice === 'spouse_and_children' ? 'border-primary bg-primary/5 scale-[1.01]' : 'border-gray-200 bg-white hover:border-primary hover:bg-primary/5 hover:scale-[1.01]'}`}
                                                >
                                                    <div className="flex items-center gap-4">
                                                        <div className={`h-6 w-6 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${interventionChoice === 'spouse_and_children' ? 'border-primary bg-primary text-white' : 'border-gray-300'}`}>
                                                            {interventionChoice === 'spouse_and_children' && <Check className="h-4 w-4" />}
                                                        </div>
                                                        <div className="flex flex-col min-w-0">
                                                            <span className="text-base font-semibold text-gray-900">Leave my estate to {spouse?.fullName || 'partner'} and {childNames || 'children'}</span>
                                                            <span className="text-sm text-muted-foreground">
                                                                I will choose the exact split between them on the next page
                                                            </span>
                                                        </div>
                                                    </div>
                                                </button>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                        </div>

                        {/* Standard navigation for intervention page */}
                        <FormNavigation
                            onBack={() => {
                                setInterventionStep('none');
                                setInterventionChoice(null);
                            }}
                            onNext={() => {
                                if (interventionChoice === 'spouse_first') {
                                    const spouse = beneficiaries?.find(b => b.relationship === 'Spouse' || b.relationship === 'Partner' || b.relationship === 'Spouse/Partner');
                                    const children = beneficiaries?.filter(b => b.relationship === 'Child');

                                    if (spouse) {
                                        const updatedBeneficiaries = [{
                                            ...spouse,
                                            percentage: 100,
                                            predeceasePolicy: 'children' as const,
                                            substitutes: []
                                        }];

                                        setValue('beneficiaries', updatedBeneficiaries);
                                        setData({
                                            beneficiaries: sanitizeBeneficiaries(updatedBeneficiaries)
                                        });

                                        setValue('formStep', 2);
                                        setInterventionStep('none');
                                        setInterventionChoice(null);
                                        window.scrollTo(0, 0);
                                    }
                                } else if (interventionChoice === 'spouse_and_children') {
                                    setInterventionStep('none');
                                    setInterventionChoice(null);

                                    const currentBeneficiaries = watch('beneficiaries');
                                    const count = currentBeneficiaries.length;
                                    const share = 100 / count;
                                    let currentSum = 0;

                                    const updatedBeneficiaries = currentBeneficiaries.map((b, index) => {
                                        let val = parseFloat(share.toFixed(2));
                                        if (index === count - 1) {
                                            val = 100 - currentSum;
                                            val = parseFloat(val.toFixed(2));
                                        }
                                        currentSum += val;
                                        return {
                                            ...b,
                                            percentage: val,
                                            predeceasePolicy: b.predeceasePolicy || (b.relationship === 'Charity' ? 'similar_goals' : 'children'),
                                            substitutes: b.substitutes || []
                                        };
                                    });

                                    setValue('beneficiaries', updatedBeneficiaries);
                                    setValue('formStep', 2);
                                    setData({ beneficiaries: sanitizeBeneficiaries(updatedBeneficiaries) });
                                    window.scrollTo(0, 0);
                                }
                            }}
                            isValid={interventionChoice !== null}
                        />
                    </div>
                )}

                {formStep === 1 && interventionStep === 'none' && (
                    <div className="space-y-10 animate-fade-in">
                        {/* Quick Add & Add New Controls */}
                        <div className="w-full md:w-1/2 space-y-4 pb-6 border-b border-gray-100">
                            {/* Toggle Selection Cards */}
                            <QuickAddPeople
                                people={getAllBeneficiaryPeople()}
                                selectedPeople={fields as any}
                                onToggle={toggleBeneficiary}
                                onEdit={openEditQuickAddModal}
                                isSelected={isBeneficiarySelected}
                                heading=""
                            />

                            {/* Generic Add Buttons */}
                            <div className="flex gap-3">
                                <ActionCard
                                    showPlusIcon
                                    onClick={() => {
                                        setAddingNewPersonMode(true);
                                    }}
                                >
                                    Add Someone New
                                </ActionCard>
                                <ActionCard
                                    showPlusIcon
                                    onClick={() => {
                                        setAddingNewCharityMode(true);
                                    }}
                                >
                                    Add Charity
                                </ActionCard>
                            </div>
                        </div>


                        {/* Progress bar only on Step 2 */}
                        {(formStep as number) === 2 && fields.length > 0 && (
                            <div className="bg-gray-50 p-4 rounded-md space-y-2 border border-gray-200 mt-6">
                                <div className="flex justify-between items-center">
                                    <span className="font-medium">Total Allocation:</span>
                                    <span className={customTotalPercentage === 100 ? 'text-success font-bold' : 'text-destructive font-bold'}>
                                        {customTotalPercentage}%
                                    </span>
                                </div>
                                <div className="w-full bg-white rounded-full h-4 overflow-hidden border border-gray-200">
                                    <div
                                        className={`h-full transition-all duration-500 ease-out ${customTotalPercentage > 100 ? 'bg-destructive' : (customTotalPercentage === 100 ? 'bg-success' : 'bg-primary')}`}
                                        style={{ width: `${Math.min(customTotalPercentage, 100)}%` }}
                                    />
                                </div>
                                {customTotalPercentage !== 100 && (
                                    <p className="text-xs text-muted-foreground">Total must equal 100% to proceed.</p>
                                )}
                            </div>
                        )}

                        {/* Validation errors for step 1 */}
                        {step1SubmitAttempted && errors.beneficiaries && (
                            <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md text-destructive mt-4">
                                <p className="font-medium">Please fix the following:</p>
                                <ul className="list-disc list-inside mt-2 text-sm">
                                    {fields.length === 0 && <li>Please add at least one beneficiary</li>}
                                    {(formStep as number) === 2 && customTotalPercentage !== 100 && <li>Total percentage must equal 100%</li>}
                                    {(errors.beneficiaries as any)?.message && <li>{(errors.beneficiaries as any).message}</li>}
                                    {/* Show specific beneficiary errors */}
                                    {Array.isArray(errors.beneficiaries) && errors.beneficiaries.map((err, i) => (
                                        err && Object.values(err).map((fieldErr: any, j) => (
                                            <li key={`${i}-${j}`}>
                                                {beneficiaries?.[i]?.fullName || `Beneficiary ${i + 1}`}: {fieldErr?.message || (typeof fieldErr === 'object' ? 'Invalid details' : fieldErr)}
                                            </li>
                                        ))
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {/* --- STAGE 2: ALLOCATION ONLY --- */}
                {formStep === 2 && (
                    <div className="animate-fade-in flex flex-col md:flex-row gap-8 items-start">
                        <div className="w-full md:w-1/2 space-y-6">
                            <div className="space-y-4">
                                {fields.map((field, index) => (
                                    <div key={field.id} className="group p-5 rounded-2xl bg-white/80 backdrop-blur-sm border border-white/20 shadow-[0_4px_20px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-300 relative">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-bold text-xl text-gray-900 group-hover:text-primary transition-colors duration-300">{beneficiaries[index].fullName}</p>
                                            </div>
                                            <div className="flex items-center gap-3 bg-gray-50/50 rounded-xl p-2 border border-gray-100 group-hover:border-cta/20 transition-colors duration-300">
                                                <Input
                                                    type="number"
                                                    {...register(`beneficiaries.${index}.percentage`)}
                                                    className="w-20 text-right font-bold text-xl bg-transparent border-none focus-visible:ring-0 p-0 h-auto text-gray-900 placeholder:text-gray-300"
                                                    min="0"
                                                    max="100"
                                                    step="0.01"
                                                    placeholder="0"
                                                    onKeyDown={(e) => {
                                                        // Prevent up/down arrow keys
                                                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(e) => {
                                                        const newValue = parseFloat(e.target.value) || 0;
                                                        // Calculate sum of other beneficiaries' percentages
                                                        const othersTotal = beneficiaries?.reduce((sum, b, i) => {
                                                            if (i === index) return sum;
                                                            return sum + (Number(b.percentage) || 0);
                                                        }, 0) || 0;
                                                        // Cap at remaining available percentage
                                                        const maxAllowed = Math.round((100 - othersTotal) * 100) / 100;
                                                        const cappedValue = Math.min(Math.max(0, newValue), maxAllowed);
                                                        setValue(`beneficiaries.${index}.percentage`, cappedValue);
                                                    }}
                                                />
                                                <span className="font-bold text-gray-400 text-lg pr-2">%</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="bg-white/60 backdrop-blur-md p-6 rounded-2xl space-y-3 border border-white/40 shadow-sm mt-8">
                                <div className="flex justify-between items-end">
                                    <div>
                                        <span className="block text-sm font-medium text-gray-500 mb-1">Total Allocated</span>
                                        <span className={`text-3xl font-bold tracking-tight ${customTotalPercentage === 100 ? 'text-primary' : 'text-gray-900'}`}>
                                            {customTotalPercentage}%
                                        </span>
                                    </div>
                                    {customTotalPercentage !== 100 && (
                                        <span className="text-sm font-medium text-destructive bg-destructive/10 px-3 py-1 rounded-full">
                                            {customTotalPercentage < 100 ? `${(100 - customTotalPercentage).toFixed(2)}% left` : `${(customTotalPercentage - 100).toFixed(2)}% over`}
                                        </span>
                                    )}
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                                    <div
                                        className={`h-full transition-all duration-700 ease-out rounded-full ${customTotalPercentage > 100 ? 'bg-destructive' : (customTotalPercentage === 100 ? 'bg-linear-to-r from-primary to-primary/80' : 'bg-gray-400')}`}
                                        style={{ width: `${Math.min(customTotalPercentage, 100)}%` }}
                                    />
                                </div>
                                {customTotalPercentage !== 100 && (
                                    <p className="text-sm text-gray-500 text-center pt-1">Total must equal 100% to proceed.</p>
                                )}
                            </div>
                        </div>

                        {/* Right Column - Pie Chart */}
                        <div className="w-full md:w-1/2 mt-8 md:mt-0 sticky top-6">
                            <div className="">
                                {(() => {
                                    const totalAllocated = beneficiaries?.reduce((sum, b) => sum + (Number(b.percentage) || 0), 0) || 0;
                                    const unallocated = Math.round((100 - totalAllocated) * 100) / 100;
                                    if (unallocated > 0) {
                                        return (
                                            <p className="text-center text-amber-600 font-semibold mb-4">
                                                Unallocated: {unallocated}%
                                            </p>
                                        );
                                    }
                                    return <div className="mb-4" />;
                                })()}

                                {/* SVG Pie Chart with Labels */}
                                {(() => {
                                    const PIE_COLORS = [
                                        '#0d9488', // Teal (primary)
                                        '#316F60', // Primary forest green
                                        '#E5A800', // Gold accent
                                        '#1a3a34', // Deep forest green
                                        '#3d7a6b', // Medium forest green
                                        '#C4960C', // Dark gold
                                        '#4a8f7d', // Light forest green
                                        '#D4AF37', // Metallic gold
                                        '#5ba38f', // Sage green
                                        '#8B7355', // Bronze accent
                                    ];

                                    const size = 400;
                                    const center = size / 2;
                                    const radius = 150;
                                    const labelRadius = radius + 40;

                                    let cumulativePercent = 0;

                                    const getCoordinatesForPercent = (percent: number) => {
                                        // Start from top (-90 degrees)
                                        const angle = 2 * Math.PI * percent - Math.PI / 2;
                                        const x = Math.cos(angle);
                                        const y = Math.sin(angle);
                                        return [x, y];
                                    };

                                    const slices = beneficiaries.map((b, i) => {
                                        const percent = (Number(b.percentage) || 0) / 100;
                                        const startPercent = cumulativePercent;
                                        const [startX, startY] = getCoordinatesForPercent(cumulativePercent);
                                        cumulativePercent += percent;
                                        const [endX, endY] = getCoordinatesForPercent(cumulativePercent);
                                        const largeArcFlag = percent > 0.5 ? 1 : 0;

                                        // Calculate midpoint angle for label positioning
                                        const midPercent = startPercent + percent / 2;
                                        const [midX, midY] = getCoordinatesForPercent(midPercent);

                                        // Handle 100% case - draw a full circle instead of an arc
                                        let pathData: string;
                                        if (percent >= 0.999) {
                                            // Full circle: use two semicircular arcs
                                            pathData = [
                                                `M ${center} ${center - radius}`,
                                                `A ${radius} ${radius} 0 1 1 ${center} ${center + radius}`,
                                                `A ${radius} ${radius} 0 1 1 ${center} ${center - radius}`,
                                                `Z`,
                                            ].join(' ');
                                        } else {
                                            pathData = [
                                                `M ${center} ${center}`,
                                                `L ${center + startX * radius} ${center + startY * radius}`,
                                                `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${center + endX * radius} ${center + endY * radius}`,
                                                `Z`,
                                            ].join(' ');
                                        }

                                        // Determine if label should be on left or right side
                                        const isRightSide = midX >= 0;

                                        // Determine display name
                                        let displayName = b.fullName || '(Name not set)';
                                        const isCharity = b.relationship === 'Charity';

                                        if (!isCharity) {
                                            // Use first name only for individuals
                                            displayName = displayName.split(' ')[0];
                                        }

                                        // Truncate if too long
                                        if (displayName.length > 15) {
                                            displayName = displayName.substring(0, 14) + '…';
                                        }

                                        return {
                                            path: pathData,
                                            color: PIE_COLORS[i % PIE_COLORS.length],
                                            name: displayName,
                                            fullName: b.fullName || '(Name not set)',
                                            relationship: b.relationship,
                                            percentage: b.percentage,
                                            labelX: center + midX * labelRadius,
                                            labelY: center + midY * labelRadius,
                                            lineStartX: center + midX * (radius - 10),
                                            lineStartY: center + midY * (radius - 10),
                                            lineEndX: center + midX * (labelRadius - 15),
                                            lineEndY: center + midY * (labelRadius - 15),
                                            isRightSide,
                                            midX,
                                            midY,
                                            percent
                                        };
                                    });

                                    return (
                                        <div className="flex flex-col items-center">
                                            <svg
                                                width={size}
                                                height={size}
                                                viewBox={`0 0 ${size} ${size}`}
                                                className="overflow-visible"
                                            >
                                                {/* Pie slices */}
                                                {slices.map((slice, i) => (
                                                    <path
                                                        key={i}
                                                        d={slice.path}
                                                        fill={slice.color}
                                                        stroke="white"
                                                        strokeWidth="2"
                                                        className="transition-all duration-300 hover:opacity-80 cursor-default"
                                                    />
                                                ))}

                                                {/* Center circle for donut effect */}
                                                <circle
                                                    cx={center}
                                                    cy={center}
                                                    r={radius * 0.45}
                                                    fill="white"
                                                />

                                                {/* Center text */}
                                                <text
                                                    x={center}
                                                    y={center - 8}
                                                    textAnchor="middle"
                                                    className="fill-gray-400 text-xs font-medium"
                                                >
                                                    Total
                                                </text>
                                                <text
                                                    x={center}
                                                    y={center + 12}
                                                    textAnchor="middle"
                                                    className="fill-teal font-bold text-lg"
                                                >
                                                    100%
                                                </text>

                                                {/* Labels with lines - only show for slices >= 5% */}
                                                {slices.filter(s => s.percent >= 0.05).map((slice, i) => (
                                                    <g key={`label-${i}`}>
                                                        {/* Line from slice to label */}
                                                        <line
                                                            x1={slice.lineStartX}
                                                            y1={slice.lineStartY}
                                                            x2={slice.lineEndX}
                                                            y2={slice.lineEndY}
                                                            stroke={slice.color}
                                                            strokeWidth="1.5"
                                                        />
                                                        {/* Percentage on the slice */}
                                                        <text
                                                            x={center + slice.midX * (radius * 0.72)}
                                                            y={center + slice.midY * (radius * 0.72)}
                                                            textAnchor="middle"
                                                            dominantBaseline="middle"
                                                            className="fill-white font-bold text-xs"
                                                            style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                                                        >
                                                            {slice.percentage}%
                                                        </text>
                                                        {/* Name label outside */}
                                                        <text
                                                            x={slice.labelX + (slice.isRightSide ? 5 : -5)}
                                                            y={slice.labelY}
                                                            textAnchor={slice.isRightSide ? 'start' : 'end'}
                                                            dominantBaseline="middle"
                                                            className="fill-gray-900 font-bold text-sm"
                                                        >
                                                            {slice.name}
                                                        </text>
                                                    </g>
                                                ))}
                                            </svg>

                                            {/* Small legend only if there are many small slices */}
                                            {slices.filter(s => s.percent < 0.05).length > 0 && (
                                                <div className="mt-4 flex flex-wrap justify-center gap-3 text-xs">
                                                    {slices.filter(s => s.percent < 0.05).map((slice, i) => (
                                                        <div key={i} className="flex items-center gap-1.5">
                                                            <div
                                                                className="w-2.5 h-2.5 rounded-full"
                                                                style={{ backgroundColor: slice.color }}
                                                            />
                                                            <span className="text-gray-600">{slice.name}</span>
                                                            <span className="font-semibold text-teal">{slice.percentage}%</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* FAQ Dropdowns */}
                            <div className="mt-6 border border-gray-200 rounded-xl p-6 bg-white">
                                <h3 className="font-bold text-primary text-xl mb-4">Common Questions</h3>
                                <div className="divide-y divide-gray-200">
                                    <details className="group py-3">
                                        <summary className="flex items-center justify-between cursor-pointer text-base font-semibold text-gray-700 hover:text-teal transition-colors">
                                            <span>Why is this split by %?</span>
                                            <ChevronDown className="h-5 w-5 text-gray-400 group-open:rotate-180 transition-transform duration-200" />
                                        </summary>
                                        <div className="text-base text-gray-600 pt-3 pb-1">
                                            <p>Splitting your estate by percentage rather than fixed amounts ensures your wishes remain fair and proportional regardless of how your estate&apos;s value changes over time.</p>
                                            <p className="mt-3">If you leave fixed sums, inflation or changes in asset values could mean some beneficiaries receive far more or less than you intended.</p>
                                        </div>
                                    </details>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* --- STAGE 3: FALLBACK QUESTIONS --- */}
                {formStep === 3 && (
                    <div className="animate-fade-in flex flex-col md:flex-row gap-8 items-start">
                        <div className="w-full md:w-1/2 space-y-6">
                            <div className="space-y-8">
                                {fields.map((field, index) => {
                                    const isCharity = beneficiaries[index].relationship === 'Charity';
                                    const policy = beneficiaries[index].predeceasePolicy;
                                    const substitutes = beneficiaries[index].substitutes || [];
                                    const subTotal = substitutes.reduce((sum, s) => sum + (parseFloat(s.percentage?.toString() || '0') || 0), 0);
                                    const isSubComplete = Math.abs(subTotal - 100) < 0.1;

                                    return (
                                        <div key={field.id} className="p-5 border-2 border-gray-200 rounded-xl bg-white relative shadow-sm">

                                            {/* Header */}
                                            <div className="pb-4 border-b border-gray-100 mb-4">
                                                <div className="flex items-center justify-between">
                                                    <div>
                                                        <p className="font-bold text-xl text-gray-900">{beneficiaries[index].fullName}</p>
                                                        {beneficiaries[index].relationship && beneficiaries[index].relationship !== 'Beneficiary' && (
                                                            <p className="text-sm text-gray-500">{beneficiaries[index].relationship}</p>
                                                        )}
                                                    </div>
                                                    <span className="font-bold text-teal text-lg">{beneficiaries[index].percentage}%</span>
                                                </div>
                                            </div>

                                            {/* Survivorship Options */}
                                            <div className="space-y-3">
                                                <p className="text-muted-foreground text-lg leading-relaxed">
                                                    {isCharity
                                                        ? `If ${beneficiaries[index].fullName} no longer exists when you die, what should happen to their share?`
                                                        : `If ${beneficiaries[index].fullName} dies before you, what should happen to their share?`
                                                    }
                                                </p>

                                                <div className="grid grid-cols-1 gap-3 pt-2">
                                                    {/* Option 1: Their children / Similar goals */}
                                                    <button
                                                        type="button"
                                                        onClick={() => setValue(`beneficiaries.${index}.predeceasePolicy`, isCharity ? 'similar_goals' : 'children')}
                                                        className={`flex items-center gap-4 px-5 py-3 border rounded-xl transition-all duration-300 text-left group ${policy === (isCharity ? 'similar_goals' : 'children') ? 'border-primary bg-primary/5 shadow-sm ring-1 ring-primary/20' : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50 bg-white'}`}
                                                    >
                                                        <div className={`h-6 w-6 rounded border flex items-center justify-center shrink-0 transition-colors ${policy === (isCharity ? 'similar_goals' : 'children') ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white group-hover:border-primary'}`}>
                                                            {policy === (isCharity ? 'similar_goals' : 'children') && <Check className="h-3.5 w-3.5" />}
                                                        </div>
                                                        <div>
                                                            <p className={`font-bold text-base ${policy === (isCharity ? 'similar_goals' : 'children') ? 'text-gray-900' : 'text-gray-700'}`}>{isCharity ? 'Pass to a Charity with similar goals' : 'Pass to their children'}</p>
                                                        </div>
                                                    </button>

                                                    {/* Option 2: Specific people or charities */}
                                                    <div className={`px-5 py-3 border rounded-xl transition-all duration-300 ${policy === 'substitute' ? (isSubComplete ? 'border-primary bg-primary/5 shadow-sm ring-1 ring-primary/20' : 'border-amber-300 bg-amber-50') : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50 bg-white'}`}>
                                                        <button
                                                            type="button"
                                                            onClick={() => setValue(`beneficiaries.${index}.predeceasePolicy`, 'substitute')}
                                                            className="flex items-center gap-4 w-full text-left group"
                                                        >
                                                            <div className={`h-6 w-6 rounded border flex items-center justify-center shrink-0 transition-colors ${policy === 'substitute' ? (isSubComplete ? 'border-primary bg-primary text-white' : 'border-amber-500 bg-amber-500 text-white') : 'border-gray-300 bg-white group-hover:border-primary'}`}>
                                                                {policy === 'substitute' && <Check className="h-3.5 w-3.5" />}
                                                            </div>
                                                            <div>
                                                                <p className={`font-bold text-base ${policy === 'substitute' ? (isSubComplete ? 'text-gray-900' : 'text-gray-900') : 'text-gray-700'}`}>Pass to {isCharity ? 'different' : 'specific'} people or charities</p>
                                                            </div>
                                                        </button>

                                                        {policy === 'substitute' && (
                                                            <div className="pl-9 pt-3 space-y-3">
                                                                {beneficiaries[index].substitutes?.map((sub, sIdx) => (
                                                                    <div key={sIdx} className="flex items-center justify-between p-2 bg-white rounded-lg border border-gray-100 shadow-sm">
                                                                        <div className="min-w-0">
                                                                            <p className="text-sm font-medium text-gray-900 truncate">{sub.fullName}</p>
                                                                            <p className="text-[10px] text-gray-500">{sub.percentage}% share of this portion</p>
                                                                        </div>
                                                                        <div className="flex gap-1 shrink-0">
                                                                            <Button
                                                                                type="button"
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                onClick={() => {
                                                                                    setTargetBeneficiaryIndex(index);
                                                                                    setEditingFallbackIndex(sIdx);
                                                                                    setFallbackModalType(sub.type === 'charity' ? 'charity' : 'individual');
                                                                                }}
                                                                                className="h-7 w-7 p-0"
                                                                            >
                                                                                <Pencil className="h-3 w-3" />
                                                                            </Button>
                                                                            <Button
                                                                                type="button"
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                onClick={() => {
                                                                                    const currentSubs = [...(beneficiaries[index].substitutes || [])];
                                                                                    currentSubs.splice(sIdx, 1);
                                                                                    setValue(`beneficiaries.${index}.substitutes`, currentSubs);
                                                                                }}
                                                                                className="h-7 w-7 p-0 text-destructive"
                                                                            >
                                                                                <UserMinus className="h-3 w-3" />
                                                                            </Button>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                                <div className="flex gap-2">
                                                                    <ActionCard
                                                                        showPlusIcon
                                                                        onClick={() => {
                                                                            setTargetBeneficiaryIndex(index);
                                                                            const currentSubs = [...(beneficiaries[index].substitutes || [])];
                                                                            const newSub = { type: 'individual' as const, fullName: '', relationship: '', percentage: 100, address: { line1: '', line2: '', city: '', postcode: '' } };
                                                                            setValue(`beneficiaries.${index}.substitutes`, [...currentSubs, newSub]);
                                                                            setEditingFallbackIndex(currentSubs.length);
                                                                            setFallbackModalType('individual');
                                                                        }}
                                                                    >
                                                                        Person
                                                                    </ActionCard>
                                                                    <ActionCard
                                                                        showPlusIcon
                                                                        onClick={() => {
                                                                            setTargetBeneficiaryIndex(index);
                                                                            const currentSubs = [...(beneficiaries[index].substitutes || [])];
                                                                            const newSub = { type: 'charity' as const, fullName: '', percentage: 100, address: { line1: '', line2: '', city: '', postcode: '' } };
                                                                            setValue(`beneficiaries.${index}.substitutes`, [...currentSubs, newSub]);
                                                                            setEditingFallbackIndex(currentSubs.length);
                                                                            setFallbackModalType('charity');
                                                                        }}
                                                                    >
                                                                        Charity
                                                                    </ActionCard>
                                                                </div>
                                                                {policy === 'substitute' && beneficiaries[index].substitutes && beneficiaries[index].substitutes!.length > 0 && (() => {
                                                                    const total = beneficiaries[index].substitutes!.reduce((sum, s) => sum + (Number(s.percentage) || 0), 0);
                                                                    if (Math.abs(total - 100) > 0.1) {
                                                                        return <p className="text-[10px] text-destructive font-medium">Total must equal 100% (Current: {total}%)</p>;
                                                                    }
                                                                    return null;
                                                                })()}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {/* Option 3: Remaining beneficiaries (Only show if more than 1 beneficiary) */}
                                                    {fields.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setValue(`beneficiaries.${index}.predeceasePolicy`, 'redistribute')}
                                                            className={`flex items-center gap-4 px-5 py-3 border rounded-xl transition-all duration-300 text-left group ${policy === 'redistribute' ? 'border-primary bg-primary/5 shadow-sm ring-1 ring-primary/20' : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50 bg-white'}`}
                                                        >
                                                            <div className={`h-6 w-6 rounded border flex items-center justify-center shrink-0 transition-colors ${policy === 'redistribute' ? 'border-primary bg-primary text-white' : 'border-gray-300 bg-white group-hover:border-primary'}`}>
                                                                {policy === 'redistribute' && <Check className="h-3.5 w-3.5" />}
                                                            </div>
                                                            <div>
                                                                <p className={`font-bold text-base ${policy === 'redistribute' ? 'text-gray-900' : 'text-gray-700'}`}>Split between remaining beneficiaries</p>
                                                            </div>
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Right Column Summary */}
                        <div className="w-full md:w-1/2 mt-8 md:mt-0 sticky top-6">
                            <div className="bg-teal/5 border border-teal/20 rounded-xl p-6">
                                <h3 className="text-base font-medium text-primary mb-4">Estate Summary</h3>
                                <div className="space-y-4">
                                    {beneficiaries.map((b, i) => (
                                        <div key={i} className="flex justify-between items-start border-b border-teal/10 pb-3 last:border-0 last:pb-0">
                                            <div>
                                                <p className="font-bold text-gray-900">{b.fullName || '(Name not set)'}</p>
                                                {b.relationship !== 'Beneficiary' && (
                                                    <p className="text-sm text-gray-600">{b.relationship}</p>
                                                )}
                                                <p className="text-xs text-gray-500 mt-1 italic">
                                                    <span className="font-semibold not-italic">Fallback: </span>
                                                    {b.predeceasePolicy === 'children' && "Pass to children"}
                                                    {b.predeceasePolicy === 'similar_goals' && "Pass to similar charity"}
                                                    {b.predeceasePolicy === 'substitute' && (
                                                        <>
                                                            Pass to specific people
                                                            {b.substitutes && b.substitutes.length > 0 && (
                                                                <span className="block not-italic text-gray-400 pl-2 mt-0.5 border-l-2 border-gray-200">
                                                                    {b.substitutes.map(s => `${s.fullName} (${s.percentage}%)`).join(', ')}
                                                                </span>
                                                            )}
                                                        </>
                                                    )}
                                                    {b.predeceasePolicy === 'redistribute' && "Split between remaining"}
                                                    {!b.predeceasePolicy && <span className="text-amber-600 font-medium not-italic">Selection required</span>}
                                                </p>
                                            </div>
                                            <span className="font-bold text-teal text-lg">{b.percentage}%</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* FAQ Dropdowns */}
                            <div className="mt-6 border border-gray-200 rounded-xl p-6 bg-white">
                                <h3 className="font-bold text-gray-900 text-xl mb-4">Common Questions</h3>
                                <div className="divide-y divide-gray-200">
                                    <details className="group py-3">
                                        <summary className="flex items-center justify-between cursor-pointer text-base font-semibold text-muted-foreground hover:text-teal transition-colors">
                                            <span>What does &apos;their children&apos; include?</span>
                                            <ChevronDown className="h-5 w-5 shrink-0 text-gray-400 group-open:rotate-180 transition-transform duration-200" />
                                        </summary>
                                        <div className="text-base text-gray-600 pt-3 pb-1">
                                            <p>&apos;Their children&apos; refers to the direct biological or legally adopted children of your named beneficiary.</p>
                                            <p className="mt-3">This means if your beneficiary passes away before you, their share will be divided equally among their own children (your potential grandchildren or the beneficiary&apos;s offspring).</p>
                                            <p className="mt-3">Stepchildren are not automatically included unless they were legally adopted.</p>
                                        </div>
                                    </details>

                                    <details className="group py-3">
                                        <summary className="flex items-center justify-between cursor-pointer text-base font-semibold text-muted-foreground hover:text-teal transition-colors">
                                            <span>What happens if primary and fallback beneficiaries predecease me?</span>
                                            <ChevronDown className="h-5 w-5 shrink-0 text-gray-400 group-open:rotate-180 transition-transform duration-200" />
                                        </summary>
                                        <div className="text-base text-gray-600 pt-3 pb-1">
                                            <p>If both your primary beneficiary and their designated fallback (e.g., their children or named substitutes) pass away before you, that share of your estate would typically fall into your &apos;residuary estate&apos;.</p>
                                            <p className="mt-3">Without any fallback provisions, intestacy rules would apply, meaning the courts decide distribution based on legal next-of-kin rules.</p>
                                        </div>
                                    </details>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {interventionStep === 'none' && (
                    <FormNavigation
                        onBack={formStep === 1 ? undefined : handleBack}
                        isValid={
                            formStep === 1
                                ? (
                                    // Step 1: At least one beneficiary
                                    fields.length > 0
                                )
                                : formStep === 2
                                    ? (
                                        // Step 2: Total must equal 100%
                                        customTotalPercentage === 100
                                    )
                                    : formStep === 3
                                        ? (
                                            // Step 3: Survivorship policies must be set for all beneficiaries
                                            beneficiaries?.every(b => {
                                                if (!b.predeceasePolicy) return false;
                                                if (b.predeceasePolicy === 'substitute') {
                                                    const total = b.substitutes?.reduce((sum, s) => sum + (Number(s.percentage) || 0), 0) || 0;
                                                    return Math.abs(total - 100) < 0.1;
                                                }
                                                return true;
                                            })
                                        )
                                        : true
                        }
                    />
                )}

                <Modal
                    isOpen={editingIndex !== null || addingNewPersonMode}
                    onClose={() => {
                        // If closing without saving and no data, remove the entry ONLY if editing
                        if (editingIndex !== null && !beneficiaries?.[editingIndex]?.fullName) {
                            // Check if we haven't typed anything in temp state either
                            if (!tempBeneficiary?.fullName) {
                                remove(editingIndex);
                            }
                        }
                        setEditingIndex(null);
                        setAddingNewPersonMode(false);
                        setTempBeneficiary(null);
                    }}
                    title={addingNewPersonMode ? "Add Person" : "Edit Beneficiary"}
                >
                    {(editingIndex !== null || addingNewPersonMode) && (() => {
                        const index = editingIndex !== null ? editingIndex : -1;
                        const currentData = index >= 0 ? beneficiaries[index] : {};

                        // Calculate max percentage for this beneficiary
                        const otherBeneficiariesTotal = beneficiaries?.reduce((sum, b, i) => {
                            if (i === index) return sum;
                            return sum + (Number(b.percentage) || 0);
                        }, 0) || 0;
                        const maxAvailable = Math.max(0, 100 - otherBeneficiariesTotal);
                        const showPercentage = !addingNewPersonMode && ((mode === 'split_custom' || mode === 'split_equal') && formStep >= 2);

                        return (
                            <AddBeneficiaryForm
                                beneficiary={currentData}
                                showPercentage={showPercentage}
                                maxPercentage={maxAvailable}
                                onSave={(data) => {
                                    if (addingNewPersonMode) {
                                        // Track and auto-select the person
                                        if (data.fullName) {
                                            const personData = {
                                                fullName: data.fullName,
                                                relationship: data.relationship || 'Beneficiary',
                                                address: data.address,
                                                isOver18: data.isOver18
                                            };
                                            trackManuallyAddedPerson(personData);
                                            // Auto-select the newly added person
                                            toggleBeneficiary(personData);
                                        }
                                        setAddingNewPersonMode(false);
                                    } else {
                                        update(index, {
                                            ...currentData,
                                            ...data,
                                            predeceasePolicy: currentData.predeceasePolicy || 'children',
                                            substitutes: currentData.substitutes || []
                                        });
                                        // Track manually added person
                                        if (data.fullName) {
                                            trackManuallyAddedPerson({
                                                fullName: data.fullName,
                                                relationship: data.relationship || 'Beneficiary',
                                                address: data.address,
                                                isOver18: data.isOver18
                                            });
                                        }
                                        setEditingIndex(null);
                                    }
                                }}
                            />
                        );
                    })()}
                </Modal>

                {/* Charity Beneficiary Modal */}
                <Modal
                    isOpen={editingCharityIndex !== null || addingNewCharityMode}
                    onClose={() => {
                        if (editingCharityIndex !== null && !beneficiaries?.[editingCharityIndex]?.fullName) {
                            remove(editingCharityIndex);
                        }
                        setEditingCharityIndex(null);
                        setAddingNewCharityMode(false);
                        setTempBeneficiary(null);
                    }}
                    title={addingNewCharityMode ? "Add Charity" : "Edit Charity"}
                >
                    {(editingCharityIndex !== null || addingNewCharityMode) && (() => {
                        const index = editingCharityIndex !== null ? editingCharityIndex : -1;
                        // Initialize temp state if null
                        const currentData = tempBeneficiary || (index >= 0 ? beneficiaries[index] : {}) || {};

                        const updateTemp = (field: string, value: any) => {
                            setTempBeneficiary((prev: any) => ({ ...prev, [field]: value }));
                        };

                        const handleSave = () => {
                            if (addingNewCharityMode) {
                                if (currentData.fullName) {
                                    const charityData = {
                                        fullName: currentData.fullName,
                                        relationship: 'Charity' as const,
                                        address: undefined,
                                        charityNumber: currentData.charityNumber
                                    };
                                    trackManuallyAddedPerson({
                                        fullName: currentData.fullName,
                                        relationship: 'Charity',
                                        address: undefined
                                    });
                                    // Auto-select the newly added charity
                                    toggleBeneficiary(charityData);
                                }
                                setAddingNewCharityMode(false);
                                setTempBeneficiary(null);
                            } else {
                                handleSaveCharity();
                            }
                        };

                        return (
                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <Label className="text-base font-medium text-gray-700">Charity Name</Label>
                                    <CharityAutocomplete
                                        onCharitySelect={(charity) => {
                                            setTempBeneficiary((prev: any) => ({
                                                ...prev,
                                                fullName: charity.name,
                                                charityNumber: charity.number
                                            }));
                                        }}
                                        defaultValue={currentData?.fullName || ''}
                                        className={index >= 0 && errors.beneficiaries?.[index]?.fullName ? 'border-destructive' : ''}
                                        // Controlled value to reflect temp state changes
                                        value={currentData?.fullName || ''}
                                        onChange={(e) => {
                                            updateTemp('fullName', e.target.value);
                                        }}
                                        name={index >= 0 ? `beneficiaries.${index}.fullName` : 'newCharityName'}
                                    />
                                    {index >= 0 && errors.beneficiaries?.[index]?.fullName && (
                                        <p className="text-destructive text-sm mt-1">Charity name is required</p>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-base font-medium text-gray-700">Charity Number (Optional)</Label>
                                    <Input
                                        value={currentData?.charityNumber || ''}
                                        onChange={(e) => updateTemp('charityNumber', e.target.value)}
                                        placeholder="e.g. 12345678"
                                    />
                                </div>

                                <div className="pt-4">
                                    <Button
                                        type="button"
                                        onClick={handleSave}
                                        disabled={!currentData?.fullName || !currentData.fullName.trim()}
                                        variant="premium"
                                        className="w-full"
                                    >
                                        Save
                                    </Button>
                                </div>
                            </div>
                        );
                    })()}
                </Modal>

                {/* Fallback Beneficiary Modal */}
                <Modal
                    isOpen={editingFallbackIndex !== null}
                    onClose={() => {
                        // If closing without saving and no data, remove the entry
                        if (editingFallbackIndex !== null && targetBeneficiaryIndex !== null) {
                            const currentSubs = beneficiaries?.[targetBeneficiaryIndex]?.substitutes;
                            // Check if we were editing a new entry (no name yet)
                            if (!currentSubs?.[editingFallbackIndex]?.fullName) {
                                const updated = [...(currentSubs || [])];
                                updated.splice(editingFallbackIndex, 1);
                                setValue(`beneficiaries.${targetBeneficiaryIndex}.substitutes`, updated);
                            }
                        }
                        setEditingFallbackIndex(null);
                        setTargetBeneficiaryIndex(null);
                        setFallbackModalType(null);
                    }}
                    title={fallbackModalType === 'charity' ? 'Add Charity' : 'Add Fallback Beneficiary'}
                >
                    {editingFallbackIndex !== null && targetBeneficiaryIndex !== null && (() => {
                        const index = editingFallbackIndex;
                        const isCharity = fallbackModalType === 'charity';
                        const currentData = beneficiaries?.[targetBeneficiaryIndex]?.substitutes?.[index] || {};

                        // Calculate allocation limits
                        const otherSubs = (beneficiaries?.[targetBeneficiaryIndex]?.substitutes || []).filter((_, i) => i !== index);
                        const otherTotal = otherSubs.reduce((sum, s) => sum + (parseFloat(s.percentage?.toString() || '0') || 0), 0);
                        const maxAllowed = parseFloat((100 - otherTotal).toFixed(2));

                        return (
                            <AddFallbackBeneficiaryForm
                                fallback={currentData}
                                isCharity={isCharity}
                                maxPercentage={maxAllowed}
                                onSave={(data) => {
                                    const currentSubs = [...(beneficiaries[targetBeneficiaryIndex].substitutes || [])];
                                    currentSubs[index] = {
                                        ...currentSubs[index],
                                        ...data
                                    };
                                    setValue(`beneficiaries.${targetBeneficiaryIndex}.substitutes`, currentSubs);
                                    setEditingFallbackIndex(null);
                                    setTargetBeneficiaryIndex(null);
                                    setFallbackModalType(null);
                                }}
                            />
                        );
                    })()}
                </Modal>

                {/* Quick Add Person Edit Modal */}
                <Modal
                    isOpen={editingQuickAddPerson !== null && tempQuickAddPerson !== null}
                    onClose={handleCloseQuickAddModal}
                    title="Edit details"
                >
                    {editingQuickAddPerson && tempQuickAddPerson && (
                        <PersonForm
                            initialData={tempQuickAddPerson}
                            onSave={(data) => {
                                updatePerson(editingQuickAddPerson.fullName, {
                                    fullName: data.fullName,
                                    address: data.address,
                                    isOver18: data.isOver18
                                });
                                handleCloseQuickAddModal();
                            }}
                            onDelete={manuallyAddedPeople.some((p: any) => p.fullName === editingQuickAddPerson.fullName) ? () => {
                                removeManuallyAddedPerson(editingQuickAddPerson.fullName);
                                handleCloseQuickAddModal();
                            } : undefined}
                            submitLabel="Save"
                            showAgeSelection={true}
                        />
                    )}
                </Modal>
            </form>
        </StepWrapper>
    );
};